import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Menu,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { Check, KeyboardArrowDown } from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import GlobaldataCompanyCard from "./GlobaldataCompanyCard";
import SearchBar from "../../components/SearchBar";
import {
  MultiSelectionAutocomplete,
  SingleSelectionAutocomplete,
} from "../../components/SelectionAutocomplete";
import {
  DEFAULT_GLOBALDATA_EXPORT_FIELDS,
  GLOBALDATA_EXPORT_FIELD_OPTIONS,
} from "../../helpers/exportFieldOptions";
import {
  EXPORT_FORMAT_OPTIONS,
  EXPORT_FORMAT_XLSX,
  getExportDownloadLabel,
  getExportFileExtension,
} from "../../helpers/exportFileFormats";
import { buildExportFilterRows } from "../../helpers/exportFilterSummary";

const PAGE_SIZE = 24;
const GD_BUCKET_CHART_HEIGHT = 360;
const SIDEBAR_CARD_SX = {
  p: 1.25,
  borderRadius: 3,
  backgroundColor: "#ffffff",
  border: "1px solid rgba(15, 23, 42, 0.06)",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
};
const SIDEBAR_TITLE_SX = {
  mb: 0.85,
  fontWeight: 700,
  fontSize: "0.95rem",
  color: "#1f2937",
};
const SIDEBAR_ACCORDION_SX = {
  border: "1px solid rgba(15, 23, 42, 0.08)",
  borderRadius: "14px !important",
  backgroundColor: "#f8fafc",
  "&:before": { display: "none" },
};
const SIDEBAR_ACCORDION_SUMMARY_SX = {
  px: 1.25,
  minHeight: 46,
  "& .MuiAccordionSummary-content": {
    my: 0.75,
  },
};
const SIDEBAR_ACCORDION_DETAILS_SX = {
  display: "grid",
  gap: 1,
  px: 1.25,
  pt: 0,
  pb: 1.25,
};
const SIDEBAR_SECTION_LABEL_SX = {
  fontWeight: 600,
  fontSize: "0.9rem",
  color: "#1f2937",
};
const SIDEBAR_SECTION_SUBTITLE_SX = {
  fontSize: "0.75rem",
  color: "#64748b",
};

function hasMeaningfulValue(value) {
  return (
    value !== undefined &&
    value !== null &&
    String(value).trim() !== "" &&
    String(value).trim().toLowerCase() !== "unknown"
  );
}

function formatCount(value) {
  return new Intl.NumberFormat("en-GB").format(Number(value || 0));
}

function buildFacetOptions(buckets, selectedValues, counts) {
  const seen = new Set();
  const values = [];

  (buckets || []).forEach((bucket) => {
    const value = typeof bucket === "string" ? bucket : bucket?.value;
    if (!hasMeaningfulValue(value) || seen.has(value)) return;
    seen.add(value);
    values.push(value);
  });

  (selectedValues || []).forEach((value) => {
    if (!hasMeaningfulValue(value) || seen.has(value)) return;
    seen.add(value);
    values.push(value);
  });

  return values.sort((left, right) => {
    const leftSelected = (selectedValues || []).includes(left);
    const rightSelected = (selectedValues || []).includes(right);
    if (leftSelected !== rightSelected) {
      return leftSelected ? -1 : 1;
    }

    const leftCount = counts?.get?.(left) ?? 0;
    const rightCount = counts?.get?.(right) ?? 0;
    if (leftCount !== rightCount) {
      return rightCount - leftCount;
    }

    return String(left).localeCompare(String(right), "en-GB", { sensitivity: "base" });
  });
}

function buildFacetCountMap(buckets) {
  return new Map((buckets || []).map((bucket) => [bucket.value, bucket.count]));
}

function mergeFacetCountMaps(currentCounts, nextCounts) {
  const mergedCounts = new Map(currentCounts || []);
  (nextCounts || new Map()).forEach((count, value) => {
    mergedCounts.set(value, count);
  });
  return mergedCounts;
}

function getFacetValues(buckets) {
  return buildFacetOptions(buckets, [], null);
}

function mergeFacetOptionValues(currentValues, nextValues) {
  const seen = new Set();
  const mergedValues = [];

  (currentValues || []).forEach((value) => {
    if (!hasMeaningfulValue(value) || seen.has(value)) return;
    seen.add(value);
    mergedValues.push(value);
  });

  (nextValues || []).forEach((value) => {
    if (!hasMeaningfulValue(value) || seen.has(value)) return;
    seen.add(value);
    mergedValues.push(value);
  });

  return mergedValues;
}

function buildChartData(buckets, limit = 8) {
  return (buckets || []).slice(0, limit).map((bucket) => ({
    label: bucket.value,
    fullLabel: bucket.value,
    count: bucket.count,
  }));
}

function getActiveBucketEntry(state) {
  return state?.activePayload?.[0]?.payload || state?.payload || state || null;
}

function getBucketChartAxisAngle(chartData) {
  const longestLabelLength = chartData.reduce(
    (maxLength, entry) => Math.max(maxLength, entry.label?.length || 0),
    0
  );

  if (longestLabelLength > 22) return -45;
  return 0;
}

const GLOBALDATA_SORT_OPTIONS = [
  { value: "companyName", label: "Company Name" },
  { value: "croScore", label: "CRO Score" },
  { value: "cdmoScore", label: "CDMO Score" },
  { value: "companyDrugCount", label: "Company Drug Count" },
  { value: "annualRevenueUsdM", label: "Annual Revenue" },
  { value: "companyStage", label: "Highest Development Stage" },
  { value: "matchedToCrunchbase", label: "Crunchbase Match" },
  { value: "matchedToCompanyHouse", label: "Company House Match" },
];
const GLOBALDATA_TIER_OPTIONS = ["Tier 1", "Tier 2", "Tier 3"];
const GLOBALDATA_DIRECTION_OPTIONS = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
];
const GLOBALDATA_MOLECULE_MATCH_OPTIONS = [
  { value: "any", label: "Any selected" },
  { value: "all", label: "Must include all selected" },
];
const GLOBALDATA_MATCH_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "crunchbase_matched", label: "Matched to Crunchbase" },
  { value: "crunchbase_unmatched", label: "Unmatched to Crunchbase" },
  { value: "companyhouse_matched", label: "Matched to Company House" },
  { value: "companyhouse_unmatched", label: "Unmatched to Company House" },
  { value: "all_unmatched", label: "Unmatched (All)" },
];
const LIST_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "client", label: "Existing Client" },
  { value: "converted", label: "Converted +" },
  { value: "contacted", label: "Contacted" },
  { value: "not-client-anymore", label: "Not client anymore" },
  { value: "non-client", label: "Non-client" },
];
const DATASET_LABELS = {
  crunchbase: "CB",
  globaldata: "GD",
  companyhouse: "CH",
};
const EMPTY_ASSIGNMENT_FILTER = "__NO_ASSIGNED_COMPANIES__";
const COMPANY_LIST_VIEW_OVERVIEW = "OVERVIEW";
const COMPANY_LIST_VIEW_ASSIGNMENTS = "ASSIGNMENTS";
const GLOBALDATA_WORKSPACE_STORAGE_KEY = "globaldata_workspace_state";

function normalizeStoredArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeGlobaldataMatchState(value) {
  return GLOBALDATA_MATCH_STATUS_OPTIONS.some((option) => option.value === value) ? value : "all";
}

function normalizeGlobaldataRightPanelView(value) {
  return value === "companies" ? "companies" : "visualisations";
}

function normalizeGlobaldataMoleculeMatch(value) {
  return value === "all" ? "all" : "any";
}

function loadStoredGlobaldataWorkspaceConfig() {
  try {
    const raw = localStorage.getItem(GLOBALDATA_WORKSPACE_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function persistStoredGlobaldataWorkspaceConfig(config) {
  try {
    localStorage.setItem(GLOBALDATA_WORKSPACE_STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Ignore storage failures and fall back to in-memory state.
  }
}

function buildClearedGlobaldataWorkspaceConfig(rightPanelView = "visualisations") {
  return {
    rightPanelView: normalizeGlobaldataRightPanelView(rightPanelView),
    searchTerm: "",
    matchedState: "all",
    clientState: "all",
    companyListView: COMPANY_LIST_VIEW_OVERVIEW,
    selectedHeadquarters: [],
    selectedEntityTypes: [],
    selectedRelationshipTypes: [],
    selectedCompanyStages: [],
    selectedCroTiers: [],
    selectedCdmoTiers: [],
    selectedTherapyAreas: [],
    selectedDrugDevelopmentStages: [],
    selectedMoleculeTypes: [],
    moleculeMatch: "any",
    selectedControlledSubstances: [],
    selectedDrugTypes: [],
    selectedDrugGeographies: [],
    selectedRouteOfAdministrations: [],
    sortBy: "companyName",
    sortDirection: "asc",
  };
}

function normalizeClientState(value) {
  return value === "client" ||
    value === "converted" ||
    value === "contacted" ||
    value === "not-client-anymore" ||
    value === "non-client"
    ? value
    : "all";
}

function getClientStateFilterLabel(value) {
  if (value === "client") return "Existing client only";
  if (value === "converted") return "Converted + only";
  if (value === "contacted") return "Contacted only";
  if (value === "not-client-anymore") return "Not client anymore only";
  if (value === "non-client") return "Non-client only";
  return "All";
}

function getClientLifecycleStatusForClientState(value) {
  if (value === "client") return "EXISTING_CLIENT";
  if (value === "converted") return "CONVERTED_CLIENT";
  if (value === "not-client-anymore") return "NOT_CLIENT_ANYMORE";
  if (value === "non-client") return "NON_CLIENT";
  return null;
}

function normalizeCompanyListView(view, clientState = "all") {
  if (view === COMPANY_LIST_VIEW_ASSIGNMENTS || clientState === "assigned") {
    return COMPANY_LIST_VIEW_ASSIGNMENTS;
  }

  return COMPANY_LIST_VIEW_OVERVIEW;
}

function SummaryCard({ label, value, tone = "default", onClick }) {
  const toneMap = {
    default: {
      background: "#ffffff",
      color: "#0f172a",
      border: "rgba(15, 23, 42, 0.06)",
    },
    matched: {
      background: "#ffffff",
      color: "#0f172a",
      border: "rgba(15, 23, 42, 0.06)",
    },
    unmatched: {
      background: "#ffffff",
      color: "#0f172a",
      border: "rgba(15, 23, 42, 0.06)",
    },
    drugs: {
      background: "#ffffff",
      color: "#0f172a",
      border: "rgba(15, 23, 42, 0.06)",
    },
  };

  const palette = toneMap[tone] || toneMap.default;

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 1.25,
        borderRadius: 3,
        backgroundColor: palette.background,
        color: palette.color,
        border: `1px solid ${palette.border}`,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Typography
        variant="body1"
        sx={{
          mb: 0.45,
          fontWeight: 600,
          color: "#1f2937",
        }}
      >
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
        {formatCount(value)}
      </Typography>
    </Paper>
  );
}

function BucketChart({
  title,
  buckets,
  emptyLabel,
  onSelect,
  color = "#1976d2",
  height = GD_BUCKET_CHART_HEIGHT,
  forceAxisAngle,
  yAxisLabel = "Count",
  yAxisWidth = 72,
  yAxisLabelOffset = 8,
}) {
  const chartData = useMemo(() => buildChartData(buckets), [buckets]);
  const axisAngle =
    forceAxisAngle === undefined ? getBucketChartAxisAngle(chartData) : forceAxisAngle;
  const handleChartClick = (state) => {
    const payload = getActiveBucketEntry(state);
    if (payload?.fullLabel && onSelect) {
      onSelect(payload.fullLabel);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        {title}
      </Typography>

      {chartData.length === 0 ? (
        <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>{emptyLabel}</Typography>
      ) : (
        <Box sx={{ width: "100%", height }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={{ left: 24, right: 16 }}
              onClick={handleChartClick}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                angle={axisAngle}
                textAnchor={axisAngle === 0 ? "middle" : "end"}
                height={axisAngle === 0 ? 50 : 120}
                interval={0}
                tick={{ fontSize: 14 }}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 14 }}
                width={yAxisWidth}
                label={{
                  value: yAxisLabel,
                  angle: -90,
                  position: "left",
                  offset: yAxisLabelOffset,
                  style: { textAnchor: "middle", fontSize: 14, fill: "#475569" },
                }}
              />
              <Tooltip formatter={(value) => formatCount(value)} />
              <Legend wrapperStyle={{ fontSize: 14 }} />
              <Bar
                dataKey="count"
                name={title}
                fill={color}
                fillOpacity={1}
                stroke={color}
                strokeWidth={1}
                isAnimationActive={false}
                onClick={handleChartClick}
                cursor={onSelect ? "pointer" : "default"}
                activeBar={onSelect ? { stroke: "#0f172a", strokeOpacity: 0.18, strokeWidth: 10 } : undefined}
                minPointSize={onSelect ? 8 : 0}
              >
                {chartData.map((entry) => (
                  <Cell
                    key={`${title}-${entry.fullLabel}`}
                    fill={color}
                    fillOpacity={1}
                    stroke={color}
                    strokeWidth={1}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}

function buildCriteriaTableRows(buckets) {
  return (buckets || []).map((bucket, index) => ({
    id: `criteria-row-${index}`,
    label: bucket?.value || "Unknown",
    count: Number(bucket?.count || 0),
  }));
}

function buildStageMoleculeRows(criteriaExtraction) {
  return (criteriaExtraction?.stageMoleculeBreakdown || []).map((row, index) => {
    const nextRow = {
      id: `stage-molecule-row-${index}`,
      developmentStage: row?.developmentStage || "Unknown",
      totalAssets: Number(row?.totalAssets || 0),
    };

    (criteriaExtraction?.moleculeTypeColumns || []).forEach((column) => {
      const matchingBucket = (row?.moleculeTypeBuckets || []).find(
        (bucket) => bucket?.value === column
      );
      nextRow[column] = Number(matchingBucket?.count || 0);
    });

    return nextRow;
  });
}

function CriteriaExtractionTable({
  columns,
  rows,
  emptyLabel,
  minHeight = 320,
  maxHeight = 320,
}) {
  if (!rows.length) {
    return (
      <Box
        sx={{
          minHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid rgba(15, 23, 42, 0.08)",
          borderRadius: 2,
          backgroundColor: "#ffffff",
          color: "#64748b",
          px: 2,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: "0.9rem" }}>{emptyLabel}</Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        width: "100%",
        maxWidth: "100%",
        minHeight,
        maxHeight,
        borderRadius: 2,
        borderColor: "rgba(15, 23, 42, 0.08)",
        overflowX: "auto",
        overflowY: "auto",
      }}
    >
      <Table stickyHeader size="small" sx={{ width: "max-content", minWidth: "100%" }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align={column.align || "left"}
                sx={{
                  backgroundColor: "#f8fafc",
                  fontWeight: 700,
                  whiteSpace: column.noWrap ? "nowrap" : "normal",
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow hover key={row.id}>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || "left"}
                  sx={{ whiteSpace: column.noWrap ? "nowrap" : "normal" }}
                >
                  {column.format ? column.format(row[column.key], row) : row[column.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CriteriaExtractionSection({
  title,
  subtitle,
  columns,
  rows,
  expanded,
  onChange,
  emptyLabel,
  minHeight,
  maxHeight,
}) {
  return (
    <Accordion
      expanded={expanded}
      onChange={(_, nextExpanded) => onChange(nextExpanded)}
      disableGutters
      sx={{
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: "14px !important",
        backgroundColor: "#ffffff",
        boxShadow: "none",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        sx={{
          px: 1.5,
          minHeight: 52,
          "& .MuiAccordionSummary-content": {
            my: 1,
          },
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>{title}</Typography>
          {subtitle ? (
            <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>{subtitle}</Typography>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0, minWidth: 0 }}>
        <CriteriaExtractionTable
          columns={columns}
          rows={rows}
          emptyLabel={emptyLabel}
          minHeight={minHeight}
          maxHeight={maxHeight}
        />
      </AccordionDetails>
    </Accordion>
  );
}

export default function GlobaldataWorkspace({
  apiEnabled,
  postJson,
  deleteJson,
  postApiFile,
  getApiJson,
  onOpenCrunchbaseMatch,
  onOpenCompanyHouseMatch,
  initialQueryConfig,
  workspaceDataset,
  onWorkspaceDatasetChange,
  savedSearches,
  savedSearchesBusy,
  onSaveSavedSearch,
  onDeleteSavedSearch,
  getAssignmentForCompanyName,
  assignedCompanyNames = [],
  assignmentView = "MY_LEADS",
  setAssignmentView = () => {},
  assignmentsLoading = false,
  canManageAssignments = false,
  assignableUsers = [],
  assignableUsersLoading = false,
  onAssignmentsChanged,
  dataRefreshToken = 0,
}) {
  const theme = useTheme();
  const useCompactRightPanelTabs = useMediaQuery(theme.breakpoints.down("sm"));
  const storedWorkspaceConfigRef = useRef(loadStoredGlobaldataWorkspaceConfig());
  const storedWorkspaceConfig = storedWorkspaceConfigRef.current || {};
  const storedSearchTerm = storedWorkspaceConfig.searchTerm || "";
  const storedMatchedState = normalizeGlobaldataMatchState(storedWorkspaceConfig.matchedState);
  const storedClientState = normalizeClientState(storedWorkspaceConfig.clientState);
  const storedCompanyListView = storedWorkspaceConfig.companyListView;
  const storedRightPanelView = normalizeGlobaldataRightPanelView(storedWorkspaceConfig.rightPanelView);
  const [rightPanelView, setRightPanelView] = useState("visualisations");
  const [workspaceDatasetMenuAnchor, setWorkspaceDatasetMenuAnchor] = useState(null);
  const [searchTerm, setSearchTerm] = useState(() => storedSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [matchedState, setMatchedState] = useState(() => storedMatchedState);
  const [clientState, setClientState] = useState(() => storedClientState);
  const [companyListView, setCompanyListView] = useState(() =>
    normalizeCompanyListView(storedCompanyListView, storedClientState)
  );
  const [selectedHeadquarters, setSelectedHeadquarters] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedHeadquarters)
  );
  const [selectedEntityTypes, setSelectedEntityTypes] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedEntityTypes)
  );
  const [selectedRelationshipTypes, setSelectedRelationshipTypes] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedRelationshipTypes)
  );
  const [selectedCompanyStages, setSelectedCompanyStages] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedCompanyStages)
  );
  const [selectedCroTiers, setSelectedCroTiers] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedCroTiers)
  );
  const [selectedCdmoTiers, setSelectedCdmoTiers] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedCdmoTiers)
  );
  const [selectedTherapyAreas, setSelectedTherapyAreas] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedTherapyAreas)
  );
  const [selectedDrugDevelopmentStages, setSelectedDrugDevelopmentStages] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedDrugDevelopmentStages)
  );
  const [selectedMoleculeTypes, setSelectedMoleculeTypes] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedMoleculeTypes)
  );
  const [moleculeMatch, setMoleculeMatch] = useState(() =>
    normalizeGlobaldataMoleculeMatch(storedWorkspaceConfig.moleculeMatch)
  );
  const [selectedControlledSubstances, setSelectedControlledSubstances] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedControlledSubstances)
  );
  const [selectedDrugTypes, setSelectedDrugTypes] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedDrugTypes)
  );
  const [selectedDrugGeographies, setSelectedDrugGeographies] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedDrugGeographies)
  );
  const [selectedRouteOfAdministrations, setSelectedRouteOfAdministrations] = useState(() =>
    normalizeStoredArray(storedWorkspaceConfig.selectedRouteOfAdministrations)
  );
  const [sortBy, setSortBy] = useState(() => storedWorkspaceConfig.sortBy || "companyName");
  const [sortDirection, setSortDirection] = useState(() =>
    storedWorkspaceConfig.sortDirection === "desc" ? "desc" : "asc"
  );
  const [exportFileName, setExportFileName] = useState("globaldata_export");
  const [criteriaExtractionFileName, setCriteriaExtractionFileName] = useState(
    "globaldata_criteria_extraction"
  );
  const [criteriaExtractionDownloadDialogOpen, setCriteriaExtractionDownloadDialogOpen] =
    useState(false);
  const [exportFormat, setExportFormat] = useState(EXPORT_FORMAT_XLSX);
  const [selectedExportFields, setSelectedExportFields] = useState(
    DEFAULT_GLOBALDATA_EXPORT_FIELDS
  );
  const [savedSearchName, setSavedSearchName] = useState("");
  const [selectedSavedSearch, setSelectedSavedSearch] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [criteriaExtraction, setCriteriaExtraction] = useState(null);
  const [criteriaExtractionLoading, setCriteriaExtractionLoading] = useState(false);
  const [criteriaExtractionExporting, setCriteriaExtractionExporting] = useState(false);
  const [criteriaExtractionError, setCriteriaExtractionError] = useState("");
  const [criteriaExtractionExpanded, setCriteriaExtractionExpanded] = useState(false);
  const [expandedCriteriaSections, setExpandedCriteriaSections] = useState({});
  const [facetOptionUniverse, setFacetOptionUniverse] = useState({
    headquarters: [],
    entityTypes: [],
    relationshipTypes: [],
    companyStages: [],
    croTiers: GLOBALDATA_TIER_OPTIONS,
    cdmoTiers: GLOBALDATA_TIER_OPTIONS,
    therapyAreas: [],
    drugDevelopmentStages: [],
    moleculeTypes: [],
    controlledSubstances: [],
    drugTypes: [],
    drugGeographies: [],
    routeOfAdministrations: [],
  });
  const [facetCountUniverse, setFacetCountUniverse] = useState({
    headquarters: new Map(),
    entityTypes: new Map(),
    relationshipTypes: new Map(),
    companyStages: new Map(),
    croTiers: new Map(),
    cdmoTiers: new Map(),
    therapyAreas: new Map(),
    drugDevelopmentStages: new Map(),
    moleculeTypes: new Map(),
    controlledSubstances: new Map(),
    drugTypes: new Map(),
    drugGeographies: new Map(),
    routeOfAdministrations: new Map(),
  });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const searchRequestSequence = useRef(0);
  const initialQueryConfigAppliedRef = useRef(false);
  const initialQueryParamsRef = useRef(
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams()
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (
      initialQueryConfigAppliedRef.current ||
      initialQueryConfig?.workspaceDataset !== "globaldata"
    ) {
      return;
    }

    const initialQueryParams = initialQueryParamsRef.current;
    const hasSearchParam =
      initialQueryParams.has("search") || initialQueryParams.has("company");
    const hasMatchedStateParam = initialQueryParams.has("matchedState");
    const hasClientStateParam = initialQueryParams.has("clientState");
    const hasViewParam = initialQueryParams.has("view");

    setSearchTerm(
      hasSearchParam ? initialQueryConfig.searchTerm || "" : storedSearchTerm
    );
    setMatchedState(
      hasMatchedStateParam ? initialQueryConfig.matchedState || "all" : storedMatchedState
    );
    setClientState(
      hasClientStateParam ? normalizeClientState(initialQueryConfig.clientState) : storedClientState
    );
    setCompanyListView(
      normalizeCompanyListView(
        initialQueryConfig.companyListView || storedCompanyListView,
        hasClientStateParam ? initialQueryConfig.clientState : storedClientState
      )
    );
    setRightPanelView(
      hasViewParam
        ? normalizeGlobaldataRightPanelView(initialQueryConfig.rightPanelView)
        : "visualisations"
    );
    setSelectedSavedSearch("");
    initialQueryConfigAppliedRef.current = true;
  }, [
    initialQueryConfig,
    storedClientState,
    storedCompanyListView,
    storedMatchedState,
    storedRightPanelView,
    storedSearchTerm,
  ]);

  useEffect(() => {
    persistStoredGlobaldataWorkspaceConfig({
      rightPanelView,
      searchTerm,
      matchedState,
      clientState,
      companyListView,
      selectedHeadquarters,
      selectedEntityTypes,
      selectedRelationshipTypes,
      selectedCompanyStages,
      selectedCroTiers,
      selectedCdmoTiers,
      selectedTherapyAreas,
      selectedDrugDevelopmentStages,
      selectedMoleculeTypes,
      moleculeMatch,
      selectedControlledSubstances,
      selectedDrugTypes,
      selectedDrugGeographies,
      selectedRouteOfAdministrations,
      sortBy,
      sortDirection,
    });
  }, [
    clientState,
    companyListView,
    matchedState,
    rightPanelView,
    searchTerm,
    selectedCdmoTiers,
    selectedCompanyStages,
    selectedControlledSubstances,
    selectedCroTiers,
    selectedDrugDevelopmentStages,
    selectedDrugGeographies,
    selectedDrugTypes,
    selectedEntityTypes,
    selectedHeadquarters,
    moleculeMatch,
    selectedMoleculeTypes,
    selectedRelationshipTypes,
    selectedRouteOfAdministrations,
    selectedTherapyAreas,
    sortBy,
    sortDirection,
  ]);

  const openWorkspaceDatasetMenu = (event) => {
    setWorkspaceDatasetMenuAnchor(event.currentTarget);
  };

  const closeWorkspaceDatasetMenu = () => {
    setWorkspaceDatasetMenuAnchor(null);
  };

  const resetAll = () => {
    const clearedWorkspaceConfig = buildClearedGlobaldataWorkspaceConfig(rightPanelView);
    persistStoredGlobaldataWorkspaceConfig(clearedWorkspaceConfig);
    storedWorkspaceConfigRef.current = clearedWorkspaceConfig;
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setMatchedState("all");
    setClientState("all");
    setCompanyListView(COMPANY_LIST_VIEW_OVERVIEW);
    setSelectedHeadquarters([]);
    setSelectedEntityTypes([]);
    setSelectedRelationshipTypes([]);
    setSelectedCompanyStages([]);
    setSelectedCroTiers([]);
    setSelectedCdmoTiers([]);
    setSelectedTherapyAreas([]);
    setSelectedDrugDevelopmentStages([]);
    setSelectedMoleculeTypes([]);
    setMoleculeMatch("any");
    setSelectedControlledSubstances([]);
    setSelectedDrugTypes([]);
    setSelectedDrugGeographies([]);
    setSelectedRouteOfAdministrations([]);
    setSortBy("companyName");
    setSortDirection("asc");
    setSelectedSavedSearch("");

    if (typeof window !== "undefined") {
      const params = new URLSearchParams();
      params.set("dataset", "globaldata");
      if (rightPanelView === "companies") {
        params.set("view", "companies");
      }
      const nextSearch = params.toString();
      const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
      initialQueryParamsRef.current = params;
      window.history.replaceState(null, "", nextUrl);
    }
  };

  const buildSavedSearchConfig = useCallback(
    () => ({
      searchTerm,
      matchedState,
      clientState,
      companyListView,
      selectedHeadquarters,
      selectedEntityTypes,
      selectedRelationshipTypes,
      selectedCompanyStages,
      selectedCroTiers,
      selectedCdmoTiers,
      selectedTherapyAreas,
      selectedDrugDevelopmentStages,
      selectedMoleculeTypes,
      moleculeMatch,
      selectedControlledSubstances,
      selectedDrugTypes,
      selectedDrugGeographies,
      selectedRouteOfAdministrations,
      sortBy,
      sortDirection,
    }),
    [
      matchedState,
      clientState,
      companyListView,
      searchTerm,
      selectedCdmoTiers,
      selectedCompanyStages,
      selectedDrugDevelopmentStages,
      selectedDrugGeographies,
      selectedDrugTypes,
      selectedControlledSubstances,
      selectedCroTiers,
      selectedEntityTypes,
      selectedHeadquarters,
      moleculeMatch,
      selectedMoleculeTypes,
      selectedRelationshipTypes,
      selectedRouteOfAdministrations,
      selectedTherapyAreas,
      sortBy,
      sortDirection,
    ]
  );

  const applySavedSearch = useCallback((config) => {
    setSearchTerm(config.searchTerm || "");
    setMatchedState(config.matchedState || "all");
    setClientState(normalizeClientState(config.clientState));
    setCompanyListView(
      normalizeCompanyListView(config.companyListView, config.clientState)
    );
    setSelectedHeadquarters(Array.isArray(config.selectedHeadquarters) ? config.selectedHeadquarters : []);
    setSelectedEntityTypes(Array.isArray(config.selectedEntityTypes) ? config.selectedEntityTypes : []);
    setSelectedRelationshipTypes(
      Array.isArray(config.selectedRelationshipTypes) ? config.selectedRelationshipTypes : []
    );
    setSelectedCompanyStages(Array.isArray(config.selectedCompanyStages) ? config.selectedCompanyStages : []);
    setSelectedCroTiers(Array.isArray(config.selectedCroTiers) ? config.selectedCroTiers : []);
    setSelectedCdmoTiers(Array.isArray(config.selectedCdmoTiers) ? config.selectedCdmoTiers : []);
    setSelectedTherapyAreas(Array.isArray(config.selectedTherapyAreas) ? config.selectedTherapyAreas : []);
    setSelectedDrugDevelopmentStages(
      Array.isArray(config.selectedDrugDevelopmentStages)
        ? config.selectedDrugDevelopmentStages
        : []
    );
    setSelectedMoleculeTypes(Array.isArray(config.selectedMoleculeTypes) ? config.selectedMoleculeTypes : []);
    setMoleculeMatch(normalizeGlobaldataMoleculeMatch(config.moleculeMatch));
    setSelectedControlledSubstances(
      Array.isArray(config.selectedControlledSubstances)
        ? config.selectedControlledSubstances
        : []
    );
    setSelectedDrugTypes(Array.isArray(config.selectedDrugTypes) ? config.selectedDrugTypes : []);
    setSelectedDrugGeographies(
      Array.isArray(config.selectedDrugGeographies) ? config.selectedDrugGeographies : []
    );
    setSelectedRouteOfAdministrations(
      Array.isArray(config.selectedRouteOfAdministrations)
        ? config.selectedRouteOfAdministrations
        : []
    );
    setSortBy(config.sortBy || "companyName");
    setSortDirection(config.sortDirection || "asc");
    setRightPanelView("visualisations");
  }, []);

  const saveCurrentSearch = useCallback(async () => {
    const trimmedName = savedSearchName.trim();
    if (!trimmedName || !onSaveSavedSearch) return;

    await onSaveSavedSearch(trimmedName, buildSavedSearchConfig());
    setSelectedSavedSearch(trimmedName);
    setSavedSearchName("");
  }, [buildSavedSearchConfig, onSaveSavedSearch, savedSearchName]);

  const loadSavedSearch = useCallback(
    (name) => {
      setSelectedSavedSearch(name);
      const match = (savedSearches || []).find((search) => search.name === name);
      if (match) {
        applySavedSearch(match.config || {});
      }
    },
    [applySavedSearch, savedSearches]
  );

  const deleteSavedSearch = useCallback(async () => {
    if (!selectedSavedSearch || !onDeleteSavedSearch) return;

    await onDeleteSavedSearch(selectedSavedSearch);
    setSelectedSavedSearch("");
  }, [onDeleteSavedSearch, selectedSavedSearch]);

  const effectiveAssignmentCompanyNames = useMemo(() => {
    if (companyListView !== COMPANY_LIST_VIEW_ASSIGNMENTS) {
      return [];
    }

    return assignedCompanyNames.length > 0
      ? assignedCompanyNames
      : [EMPTY_ASSIGNMENT_FILTER];
  }, [assignedCompanyNames, companyListView]);

  const requestBody = useMemo(
    () => ({
      search: debouncedSearchTerm,
      companyNames: effectiveAssignmentCompanyNames,
      matchedToCrunchbase:
        matchedState === "crunchbase_matched"
          ? true
          : matchedState === "crunchbase_unmatched" || matchedState === "all_unmatched"
          ? false
          : null,
      matchedToCompanyHouse:
        matchedState === "companyhouse_matched"
          ? true
          : matchedState === "companyhouse_unmatched" || matchedState === "all_unmatched"
          ? false
          : null,
      matchedToClient: null,
      contacted: clientState === "contacted" ? true : null,
      clientLifecycleStatus: getClientLifecycleStatusForClientState(clientState),
      headquarters: selectedHeadquarters,
      entityTypes: selectedEntityTypes,
      relationshipTypes: selectedRelationshipTypes,
      companyStages: selectedCompanyStages,
      croTiers: selectedCroTiers,
      cdmoTiers: selectedCdmoTiers,
      therapyAreas: selectedTherapyAreas,
      drugDevelopmentStages: selectedDrugDevelopmentStages,
      moleculeTypes: selectedMoleculeTypes,
      moleculeMatch,
      controlledSubstances: selectedControlledSubstances,
      drugTypes: selectedDrugTypes,
      drugGeographies: selectedDrugGeographies,
      routeOfAdministrations: selectedRouteOfAdministrations,
      sortBy,
      sortDirection,
      size: PAGE_SIZE,
    }),
    [
      debouncedSearchTerm,
      effectiveAssignmentCompanyNames,
      matchedState,
      clientState,
      selectedHeadquarters,
      selectedEntityTypes,
      selectedRelationshipTypes,
      selectedCompanyStages,
      selectedCroTiers,
      selectedCdmoTiers,
      selectedTherapyAreas,
      selectedDrugDevelopmentStages,
      selectedMoleculeTypes,
      moleculeMatch,
      selectedControlledSubstances,
      selectedDrugTypes,
      selectedDrugGeographies,
      selectedRouteOfAdministrations,
      sortBy,
      sortDirection,
    ]
  );

  const requestKey = JSON.stringify(requestBody);
  const globaldataExportFilters = buildExportFilterRows("GlobalData", [
    {
      label: "Match Status",
      value:
        matchedState === "crunchbase_matched"
          ? "Matched to Crunchbase only"
          : matchedState === "crunchbase_unmatched"
          ? "Unmatched to Crunchbase only"
          : matchedState === "companyhouse_matched"
          ? "Matched to Company House only"
          : matchedState === "companyhouse_unmatched"
          ? "Unmatched to Company House only"
          : matchedState === "all_unmatched"
          ? "Unmatched across all datasets"
          : "",
    },
    {
      label: "List Status",
      value: clientState !== "all" ? getClientStateFilterLabel(clientState) : "",
    },
    {
      label: "Assignments",
      value: companyListView === COMPANY_LIST_VIEW_ASSIGNMENTS ? "Assignments" : "",
    },
    { label: "Search", value: debouncedSearchTerm },
    { label: "Headquarters", value: selectedHeadquarters },
    { label: "Entity", value: selectedEntityTypes },
    { label: "Relationship", value: selectedRelationshipTypes },
    { label: "Stage", value: selectedCompanyStages },
    { label: "CRO Tier", value: selectedCroTiers },
    { label: "CDMO Tier", value: selectedCdmoTiers },
    { label: "Therapeutic Area", value: selectedTherapyAreas },
    { label: "Development Stage", value: selectedDrugDevelopmentStages },
    { label: "Molecule Type", value: selectedMoleculeTypes },
    {
      label: "Molecule Match",
      value: selectedMoleculeTypes.length > 1 && moleculeMatch === "all" ? "Must include all selected" : "",
    },
    { label: "Controlled Substance", value: selectedControlledSubstances },
    { label: "Drug Type", value: selectedDrugTypes },
    { label: "Drug Geography", value: selectedDrugGeographies },
    { label: "Route of Administration", value: selectedRouteOfAdministrations },
  ]);

  useEffect(() => {
    setCriteriaExtraction(null);
    setCriteriaExtractionError("");
    setExpandedCriteriaSections({});
  }, [requestKey]);

  const loadSearchPage = useCallback(
    async (nextPage, append) => {
      if (!apiEnabled) return;

      const requestId = ++searchRequestSequence.current;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await postJson("/api/globaldata/search", {
          ...requestBody,
          page: nextPage,
        });

        if (requestId !== searchRequestSequence.current) {
          return;
        }

        setItems((currentItems) =>
          append ? [...currentItems, ...(response.items || [])] : response.items || []
        );
        setTotal(response.total || 0);
        setSummary(response.summary || null);
        setPage(nextPage);
        setError("");
      } catch (requestError) {
        if (requestId !== searchRequestSequence.current) {
          return;
        }

        console.error("Unable to load GlobalData search results:", requestError);
        setError(requestError.message || "Unable to load GlobalData results.");
        if (!append) {
          setItems([]);
          setTotal(0);
          setSummary(null);
        }
      } finally {
        if (requestId !== searchRequestSequence.current) {
          return;
        }

        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [apiEnabled, postJson, requestBody]
  );

  useEffect(() => {
    if (
      initialQueryConfig?.workspaceDataset === "globaldata" &&
      !initialQueryConfigAppliedRef.current
    ) {
      return;
    }

    loadSearchPage(0, false);
  }, [dataRefreshToken, initialQueryConfig?.workspaceDataset, loadSearchPage, requestKey]);

  const hasMore = items.length < total;
  const activeMatchTarget = matchedState.startsWith("companyhouse_")
    ? "companyhouse"
    : "crunchbase";
  const matchedSummaryCount =
    activeMatchTarget === "companyhouse"
      ? summary?.matchedToCompanyHouseCompanies || 0
      : summary?.matchedCompanies || 0;
  const unmatchedSummaryCount =
    activeMatchTarget === "companyhouse"
      ? summary?.unmatchedToCompanyHouseCompanies || 0
      : summary?.unmatchedCompanies || 0;
  const matchedSummaryLabel =
    activeMatchTarget === "companyhouse" ? "Matched to Company House" : "Matched to Crunchbase";
  const unmatchedSummaryLabel =
    activeMatchTarget === "companyhouse"
      ? "Unmatched to Company House"
      : "Unmatched to Crunchbase";

  const exportCurrentSearchToWorkbook = useCallback(async () => {
    if (!apiEnabled || exporting || typeof postApiFile !== "function") return;

    setExporting(true);

    try {
      await postApiFile(
        "/api/globaldata/export",
        {
          search: requestBody,
          selectedFields: selectedExportFields,
          exportFilters: globaldataExportFilters,
          fileName: exportFileName,
          format: exportFormat,
        },
        `${exportFileName || "globaldata_export"}.${getExportFileExtension(exportFormat)}`
      );
    } catch (exportError) {
      console.error("Unable to export GlobalData workbook:", exportError);
      setError(exportError.message || "Unable to export GlobalData workbook.");
    } finally {
      setExporting(false);
    }
  }, [
    apiEnabled,
    exportFileName,
    exportFormat,
    exporting,
    globaldataExportFilters,
    postApiFile,
    requestBody,
    selectedExportFields,
  ]);

  const handleContactedChanged = useCallback((companyName, contacted) => {
    const normalizedCompanyName = String(companyName || "").trim().toLowerCase();
    if (!normalizedCompanyName) {
      return;
    }

    setItems((currentItems) =>
      currentItems.map((company) =>
        String(company.companyName || "").trim().toLowerCase() === normalizedCompanyName
          ? { ...company, contacted }
          : company
      )
    );
  }, []);

  const loadCriteriaExtraction = useCallback(async () => {
    if (!apiEnabled || criteriaExtractionLoading) return;

    setCriteriaExtractionLoading(true);
    setCriteriaExtractionError("");

    try {
      const response = await postJson("/api/globaldata/criteria-extraction", requestBody);
      setCriteriaExtraction(response || null);
    } catch (requestError) {
      console.error("Unable to load GlobalData criteria extraction:", requestError);
      setCriteriaExtractionError(
        requestError.message || "Unable to load the GlobalData criteria extraction tables."
      );
      setCriteriaExtraction(null);
    } finally {
      setCriteriaExtractionLoading(false);
    }
  }, [apiEnabled, criteriaExtractionLoading, postJson, requestBody]);

  const exportCriteriaExtractionWorkbook = useCallback(async () => {
    if (!apiEnabled || criteriaExtractionExporting || typeof postApiFile !== "function") return;

    setCriteriaExtractionExporting(true);
    setCriteriaExtractionError("");

    try {
      await postApiFile(
        "/api/globaldata/criteria-extraction/export",
        {
          search: requestBody,
          fileName: criteriaExtractionFileName,
        },
        `${criteriaExtractionFileName || "globaldata_criteria_extraction"}.xlsx`
      );
    } catch (exportError) {
      console.error("Unable to export GlobalData criteria extraction workbook:", exportError);
      setCriteriaExtractionError(
        exportError.message || "Unable to export the GlobalData criteria extraction workbook."
      );
    } finally {
      setCriteriaExtractionExporting(false);
    }
  }, [
    apiEnabled,
    criteriaExtractionExporting,
    criteriaExtractionFileName,
    postApiFile,
    requestBody,
  ]);

  const openCriteriaExtractionDownloadDialog = useCallback(() => {
    setCriteriaExtractionDownloadDialogOpen(true);
  }, []);

  const closeCriteriaExtractionDownloadDialog = useCallback(() => {
    if (criteriaExtractionExporting) return;
    setCriteriaExtractionDownloadDialogOpen(false);
  }, [criteriaExtractionExporting]);

  const submitCriteriaExtractionDownload = useCallback(async () => {
    await exportCriteriaExtractionWorkbook();
    setCriteriaExtractionDownloadDialogOpen(false);
  }, [exportCriteriaExtractionWorkbook]);

  useEffect(() => {
    if (rightPanelView !== "visualisations") return;
    loadCriteriaExtraction();
  }, [loadCriteriaExtraction, rightPanelView]);

  useEffect(() => {
    if (!summary) return;

    setFacetOptionUniverse((currentUniverse) => ({
      headquarters: mergeFacetOptionValues(
        currentUniverse.headquarters,
        getFacetValues(summary.headquartersBuckets)
      ),
      entityTypes: mergeFacetOptionValues(
        currentUniverse.entityTypes,
        getFacetValues(summary.entityTypeBuckets)
      ),
      relationshipTypes: mergeFacetOptionValues(
        currentUniverse.relationshipTypes,
        getFacetValues(summary.relationshipTypeBuckets)
      ),
      companyStages: mergeFacetOptionValues(
        currentUniverse.companyStages,
        getFacetValues(summary.companyStageBuckets)
      ),
      croTiers: mergeFacetOptionValues(
        currentUniverse.croTiers,
        getFacetValues(summary.croTierBuckets)
      ),
      cdmoTiers: mergeFacetOptionValues(
        currentUniverse.cdmoTiers,
        getFacetValues(summary.cdmoTierBuckets)
      ),
      therapyAreas: mergeFacetOptionValues(
        currentUniverse.therapyAreas,
        getFacetValues(summary.therapyAreaBuckets)
      ),
      drugDevelopmentStages: mergeFacetOptionValues(
        currentUniverse.drugDevelopmentStages,
        getFacetValues(summary.drugDevelopmentStageBuckets)
      ),
      moleculeTypes: mergeFacetOptionValues(
        currentUniverse.moleculeTypes,
        getFacetValues(summary.moleculeTypeBuckets)
      ),
      controlledSubstances: mergeFacetOptionValues(
        currentUniverse.controlledSubstances,
        getFacetValues(summary.controlledSubstanceBuckets)
      ),
      drugTypes: mergeFacetOptionValues(
        currentUniverse.drugTypes,
        getFacetValues(summary.drugTypeBuckets)
      ),
      drugGeographies: mergeFacetOptionValues(
        currentUniverse.drugGeographies,
        getFacetValues(summary.drugGeographyBuckets)
      ),
      routeOfAdministrations: mergeFacetOptionValues(
        currentUniverse.routeOfAdministrations,
        getFacetValues(summary.routeOfAdministrationBuckets)
      ),
    }));
    setFacetCountUniverse((currentCounts) => ({
      headquarters: mergeFacetCountMaps(
        currentCounts.headquarters,
        buildFacetCountMap(summary.headquartersBuckets)
      ),
      entityTypes: mergeFacetCountMaps(
        currentCounts.entityTypes,
        buildFacetCountMap(summary.entityTypeBuckets)
      ),
      relationshipTypes: mergeFacetCountMaps(
        currentCounts.relationshipTypes,
        buildFacetCountMap(summary.relationshipTypeBuckets)
      ),
      companyStages: mergeFacetCountMaps(
        currentCounts.companyStages,
        buildFacetCountMap(summary.companyStageBuckets)
      ),
      croTiers: mergeFacetCountMaps(
        currentCounts.croTiers,
        buildFacetCountMap(summary.croTierBuckets)
      ),
      cdmoTiers: mergeFacetCountMaps(
        currentCounts.cdmoTiers,
        buildFacetCountMap(summary.cdmoTierBuckets)
      ),
      therapyAreas: mergeFacetCountMaps(
        currentCounts.therapyAreas,
        buildFacetCountMap(summary.therapyAreaBuckets)
      ),
      drugDevelopmentStages: mergeFacetCountMaps(
        currentCounts.drugDevelopmentStages,
        buildFacetCountMap(summary.drugDevelopmentStageBuckets)
      ),
      moleculeTypes: mergeFacetCountMaps(
        currentCounts.moleculeTypes,
        buildFacetCountMap(summary.moleculeTypeBuckets)
      ),
      controlledSubstances: mergeFacetCountMaps(
        currentCounts.controlledSubstances,
        buildFacetCountMap(summary.controlledSubstanceBuckets)
      ),
      drugTypes: mergeFacetCountMaps(
        currentCounts.drugTypes,
        buildFacetCountMap(summary.drugTypeBuckets)
      ),
      drugGeographies: mergeFacetCountMaps(
        currentCounts.drugGeographies,
        buildFacetCountMap(summary.drugGeographyBuckets)
      ),
      routeOfAdministrations: mergeFacetCountMaps(
        currentCounts.routeOfAdministrations,
        buildFacetCountMap(summary.routeOfAdministrationBuckets)
      ),
    }));
  }, [summary]);

  const facetData = useMemo(() => {
    const nextSummary = summary || {};
    const headquartersCounts = mergeFacetCountMaps(
      facetCountUniverse.headquarters,
      buildFacetCountMap(nextSummary.headquartersBuckets)
    );
    const entityTypeCounts = mergeFacetCountMaps(
      facetCountUniverse.entityTypes,
      buildFacetCountMap(nextSummary.entityTypeBuckets)
    );
    const relationshipTypeCounts = mergeFacetCountMaps(
      facetCountUniverse.relationshipTypes,
      buildFacetCountMap(nextSummary.relationshipTypeBuckets)
    );
    const companyStageCounts = mergeFacetCountMaps(
      facetCountUniverse.companyStages,
      buildFacetCountMap(nextSummary.companyStageBuckets)
    );
    const croTierCounts = mergeFacetCountMaps(
      facetCountUniverse.croTiers,
      buildFacetCountMap(nextSummary.croTierBuckets)
    );
    const cdmoTierCounts = mergeFacetCountMaps(
      facetCountUniverse.cdmoTiers,
      buildFacetCountMap(nextSummary.cdmoTierBuckets)
    );
    const therapyAreaCounts = mergeFacetCountMaps(
      facetCountUniverse.therapyAreas,
      buildFacetCountMap(nextSummary.therapyAreaBuckets)
    );
    const drugDevelopmentStageCounts = mergeFacetCountMaps(
      facetCountUniverse.drugDevelopmentStages,
      buildFacetCountMap(nextSummary.drugDevelopmentStageBuckets)
    );
    const moleculeTypeCounts = mergeFacetCountMaps(
      facetCountUniverse.moleculeTypes,
      buildFacetCountMap(nextSummary.moleculeTypeBuckets)
    );
    const controlledSubstanceCounts = mergeFacetCountMaps(
      facetCountUniverse.controlledSubstances,
      buildFacetCountMap(nextSummary.controlledSubstanceBuckets)
    );
    const drugTypeCounts = mergeFacetCountMaps(
      facetCountUniverse.drugTypes,
      buildFacetCountMap(nextSummary.drugTypeBuckets)
    );
    const drugGeographyCounts = mergeFacetCountMaps(
      facetCountUniverse.drugGeographies,
      buildFacetCountMap(nextSummary.drugGeographyBuckets)
    );
    const routeOfAdministrationCounts = mergeFacetCountMaps(
      facetCountUniverse.routeOfAdministrations,
      buildFacetCountMap(nextSummary.routeOfAdministrationBuckets)
    );

    return {
      headquarters: {
        options: buildFacetOptions(
          facetOptionUniverse.headquarters,
          selectedHeadquarters,
          headquartersCounts
        ),
        counts: headquartersCounts,
      },
      entityTypes: {
        options: buildFacetOptions(
          facetOptionUniverse.entityTypes,
          selectedEntityTypes,
          entityTypeCounts
        ),
        counts: entityTypeCounts,
      },
      relationshipTypes: {
        options: buildFacetOptions(
          facetOptionUniverse.relationshipTypes,
          selectedRelationshipTypes,
          relationshipTypeCounts
        ),
        counts: relationshipTypeCounts,
      },
      companyStages: {
        options: buildFacetOptions(
          facetOptionUniverse.companyStages,
          selectedCompanyStages,
          companyStageCounts
        ),
        counts: companyStageCounts,
      },
      croTiers: {
        options: buildFacetOptions(
          facetOptionUniverse.croTiers,
          selectedCroTiers,
          croTierCounts
        ),
        counts: croTierCounts,
      },
      cdmoTiers: {
        options: buildFacetOptions(
          facetOptionUniverse.cdmoTiers,
          selectedCdmoTiers,
          cdmoTierCounts
        ),
        counts: cdmoTierCounts,
      },
      therapyAreas: {
        options: buildFacetOptions(
          facetOptionUniverse.therapyAreas,
          selectedTherapyAreas,
          therapyAreaCounts
        ),
        counts: therapyAreaCounts,
      },
      drugDevelopmentStages: {
        options: buildFacetOptions(
          facetOptionUniverse.drugDevelopmentStages,
          selectedDrugDevelopmentStages,
          drugDevelopmentStageCounts
        ),
        counts: drugDevelopmentStageCounts,
      },
      moleculeTypes: {
        options: buildFacetOptions(
          facetOptionUniverse.moleculeTypes,
          selectedMoleculeTypes,
          moleculeTypeCounts
        ),
        counts: moleculeTypeCounts,
      },
      controlledSubstances: {
        options: buildFacetOptions(
          facetOptionUniverse.controlledSubstances,
          selectedControlledSubstances,
          controlledSubstanceCounts
        ),
        counts: controlledSubstanceCounts,
      },
      drugTypes: {
        options: buildFacetOptions(
          facetOptionUniverse.drugTypes,
          selectedDrugTypes,
          drugTypeCounts
        ),
        counts: drugTypeCounts,
      },
      drugGeographies: {
        options: buildFacetOptions(
          facetOptionUniverse.drugGeographies,
          selectedDrugGeographies,
          drugGeographyCounts
        ),
        counts: drugGeographyCounts,
      },
      routeOfAdministrations: {
        options: buildFacetOptions(
          facetOptionUniverse.routeOfAdministrations,
          selectedRouteOfAdministrations,
          routeOfAdministrationCounts
        ),
        counts: routeOfAdministrationCounts,
      },
    };
  }, [
    selectedCdmoTiers,
    selectedCompanyStages,
    selectedCroTiers,
    selectedDrugDevelopmentStages,
    selectedDrugGeographies,
    selectedDrugTypes,
    selectedControlledSubstances,
    selectedEntityTypes,
    facetCountUniverse,
    facetOptionUniverse,
    selectedHeadquarters,
    selectedMoleculeTypes,
    selectedRelationshipTypes,
    selectedRouteOfAdministrations,
    selectedTherapyAreas,
    summary,
  ]);

  const criteriaExtractionSections = useMemo(() => {
    const countFormatter = (value) => formatCount(value);
    const countColumn = (label, countLabel) => [
      { key: "label", label },
      { key: "count", label: countLabel, align: "right", format: countFormatter },
    ];

    return [
      {
        id: "asset-distribution",
        title: "Asset Distribution",
        subtitle: "Number of assets per molecular type",
        columns: countColumn("Molecule Type", "Assets"),
        rows: buildCriteriaTableRows(criteriaExtraction?.assetDistribution),
        emptyLabel: "No asset distribution available for the selected criteria.",
      },
      {
        id: "cro-tier-distribution",
        title: "CRO Tier Distribution",
        subtitle: "Number of companies per CRO tier",
        columns: countColumn("CRO Tier", "Companies"),
        rows: buildCriteriaTableRows(criteriaExtraction?.croTierDistribution),
        emptyLabel: "No CRO tier distribution available for the selected criteria.",
        minHeight: 0,
        maxHeight: 220,
      },
      {
        id: "cdmo-tier-distribution",
        title: "CDMO Tier Distribution",
        subtitle: "Number of companies per CDMO tier",
        columns: countColumn("CDMO Tier", "Companies"),
        rows: buildCriteriaTableRows(criteriaExtraction?.cdmoTierDistribution),
        emptyLabel: "No CDMO tier distribution available for the selected criteria.",
        minHeight: 0,
        maxHeight: 220,
      },
      {
        id: "development-stage",
        title: "Development Stage Drug Summary",
        subtitle: "Number of drugs per development stage",
        columns: countColumn("Development Stage", "Assets"),
        rows: buildCriteriaTableRows(criteriaExtraction?.developmentStageDistribution),
        emptyLabel: "No development stage summary available for the selected criteria.",
      },
      {
        id: "stage-by-molecule",
        title: "Stage by Molecule Type",
        subtitle: "Breakdown of molecule types within each development stage",
        columns: [
          { key: "developmentStage", label: "Development Stage" },
          {
            key: "totalAssets",
            label: "Total Assets",
            align: "right",
            format: countFormatter,
          },
          ...((criteriaExtraction?.moleculeTypeColumns || []).map((column) => ({
            key: column,
            label: column,
            align: "right",
            format: countFormatter,
          }))),
        ],
        rows: buildStageMoleculeRows(criteriaExtraction),
        emptyLabel: "No stage-by-molecule breakdown available for the selected criteria.",
        minHeight: 360,
        maxHeight: 360,
      },
      {
        id: "therapeutic-area",
        title: "Therapeutic Area Distribution",
        subtitle: "Number of assets per therapeutic area",
        columns: countColumn("Therapeutic Area", "Assets"),
        rows: buildCriteriaTableRows(criteriaExtraction?.therapeuticAreaDistribution),
        emptyLabel: "No therapeutic area distribution available for the selected criteria.",
      },
      {
        id: "geographic-distribution",
        title: "Geographic Distribution",
        subtitle: "Number of companies per region",
        columns: countColumn("Region", "Companies"),
        rows: buildCriteriaTableRows(criteriaExtraction?.geographicDistribution),
        emptyLabel: "No geographic distribution available for the selected criteria.",
      },
    ].map((section) => ({
      ...section,
      minHeight: section.minHeight ?? 320,
      maxHeight: section.maxHeight ?? 320,
    }));
  }, [criteriaExtraction]);

  const toggleFacetSelection = (setter, value) => {
    setter((currentValues) =>
      currentValues.includes(value)
        ? currentValues.filter((entry) => entry !== value)
        : [...currentValues, value]
    );
  };

  const handleBucketSelection = (filterType, value) => {
    if (!value) return;

    if (filterType === "headquarters") {
      toggleFacetSelection(setSelectedHeadquarters, value);
      return;
    }

    if (filterType === "entityTypes") {
      toggleFacetSelection(setSelectedEntityTypes, value);
      return;
    }

    if (filterType === "companyStages") {
      toggleFacetSelection(setSelectedCompanyStages, value);
      return;
    }

    if (filterType === "croTiers") {
      toggleFacetSelection(setSelectedCroTiers, value);
      return;
    }

    if (filterType === "cdmoTiers") {
      toggleFacetSelection(setSelectedCdmoTiers, value);
      return;
    }

    if (filterType === "therapyAreas") {
      toggleFacetSelection(setSelectedTherapyAreas, value);
      return;
    }

    if (filterType === "drugDevelopmentStages") {
      toggleFacetSelection(setSelectedDrugDevelopmentStages, value);
      return;
    }

    if (filterType === "moleculeTypes") {
      toggleFacetSelection(setSelectedMoleculeTypes, value);
      return;
    }

    if (filterType === "controlledSubstances") {
      toggleFacetSelection(setSelectedControlledSubstances, value);
    }
  };

  if (!apiEnabled) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        GlobalData requires API mode because this workspace is backed by the new server-side
        search, details and drugs endpoints.
      </Alert>
    );
  }

  return (
    <>
      <Box
        sx={{
          position: { xs: "static", md: "fixed" },
          top: { md: 76 },
          left: { md: 0 },
          right: { md: 0 },
          zIndex: 12,
          mb: 0.25,
        }}
      >
        <Paper
          sx={{
            py: 1,
            borderRadius: 0,
            backgroundColor: "#ffffff",
            border: "none",
            boxShadow: "none",
          }}
        >
                <Container maxWidth="xl">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, md: 1.5 },
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    width: "100%",
                    px: { xs: 0, md: 1.5 },
                  }}
                >
                  <Typography
                    sx={{
                      display: "block",
                      color: "#94a3b8",
                      fontSize: { xs: "0.72rem", md: "0.8rem" },
                      fontWeight: 700,
                      letterSpacing: { xs: "0.05em", md: "0.08em" },
                      textTransform: "uppercase",
                      pl: { xs: 0, md: 1.5 },
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    Workspace View
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "nowrap",
                      marginLeft: "auto",
                      gap: 1,
                      width: "fit-content",
                      maxWidth: "100%",
                      minWidth: 0,
                      flexShrink: 0,
                    }}
                  >
                    <Button
                      variant="outlined"
                      endIcon={<KeyboardArrowDown fontSize="small" />}
                      onClick={openWorkspaceDatasetMenu}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 999,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        px: { xs: 0.8, md: 1.5 },
                        minWidth: { xs: 82, md: 128 },
                        fontSize: { xs: "0.74rem", md: "0.88rem" },
                      }}
                    >
                      {useCompactRightPanelTabs
                        ? `DS: ${DATASET_LABELS[workspaceDataset] || "CB"}`
                        : `Data Source: ${DATASET_LABELS[workspaceDataset] || "CB"}`}
                    </Button>
                    <Tabs
                      value={rightPanelView}
                      onChange={(_, value) => setRightPanelView(value)}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        flex: "0 0 auto",
                        display: "inline-flex",
                        minWidth: 0,
                        flexShrink: 0,
                        minHeight: "auto",
                        width: "fit-content",
                        maxWidth: "100%",
                        "& .MuiTabs-scroller": {
                          overflow: "visible !important",
                        },
                        "& .MuiTabs-indicator": { display: "none" },
                        "& .MuiTabs-flexContainer": {
                          gap: 0.5,
                          flexWrap: "nowrap",
                        },
                        "& .MuiTab-root": {
                          minHeight: { xs: 34, md: 38 },
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: { xs: "0.74rem", md: "0.88rem" },
                          borderRadius: 999,
                          px: { xs: 0.9, md: 1.6 },
                          py: 0.5,
                          color: "#475569",
                          minWidth: { xs: 92, md: 136 },
                        },
                        "& .Mui-selected": {
                          color: "#0f172a !important",
                          backgroundColor: "#eef2f7",
                        },
                      }}
                    >
                      <Tab label="Visualisations" value="visualisations" />
                      <Tab label="Company List" value="companies" />
                    </Tabs>
                    <Menu
                      anchorEl={workspaceDatasetMenuAnchor}
                      open={Boolean(workspaceDatasetMenuAnchor)}
                      onClose={closeWorkspaceDatasetMenu}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      PaperProps={{
                        sx: {
                          mt: 1,
                          minWidth: 220,
                          borderRadius: 2,
                          boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
                        },
                      }}
                    >
                      <MenuItem
                        selected={workspaceDataset === "crunchbase"}
                        onClick={() => {
                          onWorkspaceDatasetChange?.("crunchbase");
                          closeWorkspaceDatasetMenu();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {workspaceDataset === "crunchbase" ? <Check fontSize="small" /> : null}
                        </ListItemIcon>
                        <ListItemText primary="Crunchbase" />
                      </MenuItem>
                      <MenuItem
                        selected={workspaceDataset === "globaldata"}
                        onClick={() => {
                          onWorkspaceDatasetChange?.("globaldata");
                          closeWorkspaceDatasetMenu();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {workspaceDataset === "globaldata" ? <Check fontSize="small" /> : null}
                        </ListItemIcon>
                        <ListItemText primary="GlobalData" />
                      </MenuItem>
                      <MenuItem
                        selected={workspaceDataset === "companyhouse"}
                        onClick={() => {
                          onWorkspaceDatasetChange?.("companyhouse");
                          closeWorkspaceDatasetMenu();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {workspaceDataset === "companyhouse" ? <Check fontSize="small" /> : null}
                        </ListItemIcon>
                        <ListItemText primary="Company House" />
                      </MenuItem>
                    </Menu>
                  </Box>
                </Box>
              </Container>
            </Paper>
          </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "340px minmax(0, 1fr)" },
          gap: { xs: 1.5, md: 2, lg: 2.5 },
          alignItems: "start",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "none", lg: 340 },
            justifySelf: { xs: "stretch", lg: "center" },
            alignSelf: { lg: "start" },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gap: 1,
              position: { xs: "static", lg: "fixed" },
              top: { lg: 152 },
              left: { lg: "max(24px, calc((100vw - 1536px) / 2 + 24px))" },
              width: { xs: "100%", lg: 340 },
              maxWidth: { lg: 340 },
              boxSizing: "border-box",
              alignContent: { lg: "start" },
              maxHeight: { lg: "calc(100vh - 188px)" },
              overflowY: { lg: "auto" },
              pr: { lg: 0.5 },
              scrollbarGutter: { lg: "stable" },
            }}
          >
            <Paper
              sx={SIDEBAR_CARD_SX}
            >
              <Typography sx={SIDEBAR_TITLE_SX}>
                Match Status
              </Typography>
              <SingleSelectionAutocomplete
                label="Match Status"
                value={matchedState}
                onChange={(value) => setMatchedState(value || "all")}
                options={GLOBALDATA_MATCH_STATUS_OPTIONS}
              />
            </Paper>

            <Paper
              sx={SIDEBAR_CARD_SX}
            >
              <Typography sx={SIDEBAR_TITLE_SX}>
                List Status
              </Typography>
              <SingleSelectionAutocomplete
                label="List Status"
                value={clientState}
                onChange={(value) => setClientState(value || "all")}
                options={LIST_STATUS_OPTIONS}
              />
            </Paper>

            <Paper
              sx={{
                p: 1,
                borderRadius: 3,
                backgroundColor: "#ffffff",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Typography sx={{ ...SIDEBAR_TITLE_SX, mb: 1 }}>
                Company List
              </Typography>
              <Tabs
                value={companyListView}
                onChange={(event, nextValue) => setCompanyListView(nextValue)}
                orientation="vertical"
                variant="scrollable"
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  minHeight: "auto",
                  "& .MuiTabs-flexContainer": {
                    alignItems: "stretch",
                  },
                  "& .MuiTab-root": {
                    alignItems: "flex-start",
                    textAlign: "left",
                    minHeight: 36,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    lineHeight: 1.2,
                    borderRadius: 2,
                    mx: 0.5,
                    px: 1,
                    py: 0.5,
                  },
                }}
              >
                <Tab label="Overview" value={COMPANY_LIST_VIEW_OVERVIEW} />
                <Tab label="Assignments" value={COMPANY_LIST_VIEW_ASSIGNMENTS} />
              </Tabs>
            </Paper>

            {companyListView === COMPANY_LIST_VIEW_ASSIGNMENTS && (
              <Paper sx={SIDEBAR_CARD_SX}>
                <Typography sx={SIDEBAR_TITLE_SX}>Assignment View</Typography>
                <Box sx={{ display: "grid", gap: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Scope</InputLabel>
                    <Select
                      value={assignmentView}
                      label="Scope"
                      onChange={(event) => setAssignmentView(event.target.value)}
                    >
                      <MenuItem value="MY_LEADS">My Leads</MenuItem>
                      <MenuItem value="ALL" disabled={!canManageAssignments}>
                        All Assignments
                      </MenuItem>
                    </Select>
                  </FormControl>
                  {assignmentsLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#64748b" }}>
                      <CircularProgress size={16} />
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        Loading assignments...
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
                      {assignmentView === "ALL" && canManageAssignments
                        ? "Shows every currently assigned company."
                        : "Shows companies assigned to your user."}
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}

            <Paper
              sx={SIDEBAR_CARD_SX}
            >
              <Accordion disableGutters elevation={0} sx={SIDEBAR_ACCORDION_SX}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={SIDEBAR_ACCORDION_SUMMARY_SX}>
                  <Box>
                    <Typography sx={SIDEBAR_SECTION_LABEL_SX}>
                      Export
                    </Typography>
                    <Typography sx={SIDEBAR_SECTION_SUBTITLE_SX}>
                      {selectedExportFields.length} fields selected
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={SIDEBAR_ACCORDION_DETAILS_SX}>
                  <TextField
                    size="small"
                    label="File Name"
                    value={exportFileName}
                    onChange={(event) => setExportFileName(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FileDownloadOutlinedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl size="small">
                    <InputLabel id="globaldata-export-format-label">Format</InputLabel>
                    <Select
                      labelId="globaldata-export-format-label"
                      label="Format"
                      value={exportFormat}
                      onChange={(event) => setExportFormat(event.target.value)}
                    >
                      {EXPORT_FORMAT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <MultiSelectionAutocomplete
                    label="Fields"
                    values={selectedExportFields}
                    onChange={setSelectedExportFields}
                    options={GLOBALDATA_EXPORT_FIELD_OPTIONS}
                    showCheckboxes
                  />
                  <Button
                    variant="contained"
                    color="success"
                    onClick={exportCurrentSearchToWorkbook}
                    disabled={exporting || selectedExportFields.length === 0}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 999,
                      boxShadow: "none",
                    }}
                  >
                    {exporting ? "Exporting..." : `Download ${getExportDownloadLabel(exportFormat)}`}
                  </Button>
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper
              sx={{ ...SIDEBAR_CARD_SX, mt: 0.75 }}
            >
              <Typography sx={SIDEBAR_TITLE_SX}>
                Search and Saved Views
              </Typography>
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                savedSearchName={savedSearchName}
                setSavedSearchName={setSavedSearchName}
                saveCurrentSearch={saveCurrentSearch}
                savedSearches={savedSearches || []}
                selectedSavedSearch={selectedSavedSearch}
                loadSavedSearch={loadSavedSearch}
                deleteSavedSearch={deleteSavedSearch}
                savedSearchesBusy={savedSearchesBusy}
                searchLabel="Company search"
                searchPlaceholder="Search company name or overview"
                saveNameLabel="Save view as..."
                loadLabel="Load view"
                saveButtonLabel="Save View"
              />
            </Paper>

            <Paper
              sx={SIDEBAR_CARD_SX}
            >
              <Typography sx={SIDEBAR_TITLE_SX}>
                Filters
              </Typography>

              <Box sx={{ display: "grid", gap: 1 }}>
                <Accordion disableGutters elevation={0} sx={SIDEBAR_ACCORDION_SX}>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={SIDEBAR_ACCORDION_SUMMARY_SX}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={SIDEBAR_SECTION_LABEL_SX}>Scoring filters</Typography>
                      <Typography sx={SIDEBAR_SECTION_SUBTITLE_SX}>
                        Independent CRO and CDMO tiers
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={SIDEBAR_ACCORDION_DETAILS_SX}>
                    <MultiSelectionAutocomplete
                      label="CRO Tier"
                      values={selectedCroTiers}
                      onChange={setSelectedCroTiers}
                      options={facetData.croTiers.options}
                      counts={facetData.croTiers.counts}
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="CDMO Tier"
                      values={selectedCdmoTiers}
                      onChange={setSelectedCdmoTiers}
                      options={facetData.cdmoTiers.options}
                      counts={facetData.cdmoTiers.counts}
                      showCheckboxes
                    />
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", lineHeight: 1.45 }}>
                      Multiple tiers within one model use OR. CRO, CDMO, and all other
                      filters combine with AND.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion disableGutters elevation={0} sx={SIDEBAR_ACCORDION_SX}>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={SIDEBAR_ACCORDION_SUMMARY_SX}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={SIDEBAR_SECTION_LABEL_SX}>Company filters</Typography>
                      <Typography sx={SIDEBAR_SECTION_SUBTITLE_SX}>
                        Company attributes and relationships
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={SIDEBAR_ACCORDION_DETAILS_SX}>
                    <MultiSelectionAutocomplete
                      label="Headquarters"
                      values={selectedHeadquarters}
                      onChange={setSelectedHeadquarters}
                      options={facetData.headquarters.options}
                      counts={facetData.headquarters.counts}
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="Entity Type"
                      values={selectedEntityTypes}
                      onChange={setSelectedEntityTypes}
                      options={facetData.entityTypes.options}
                      counts={facetData.entityTypes.counts}
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="Relationship Type"
                      values={selectedRelationshipTypes}
                      onChange={setSelectedRelationshipTypes}
                      options={facetData.relationshipTypes.options}
                      counts={facetData.relationshipTypes.counts}
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="Highest Development Stage"
                      values={selectedCompanyStages}
                      onChange={setSelectedCompanyStages}
                      options={facetData.companyStages.options}
                      counts={facetData.companyStages.counts}
                      showCheckboxes
                    />
                  </AccordionDetails>
                </Accordion>

                <Accordion disableGutters elevation={0} sx={SIDEBAR_ACCORDION_SX}>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={SIDEBAR_ACCORDION_SUMMARY_SX}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={SIDEBAR_SECTION_LABEL_SX}>Drug filters</Typography>
                      <Typography sx={SIDEBAR_SECTION_SUBTITLE_SX}>
                        Therapy, molecule, and development details
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={SIDEBAR_ACCORDION_DETAILS_SX}>
                    <MultiSelectionAutocomplete
                      label="Therapeutic Areas"
                      values={selectedTherapyAreas}
                      onChange={setSelectedTherapyAreas}
                      options={facetData.therapyAreas.options}
                      counts={facetData.therapyAreas.counts}
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="Development Stage"
                      values={selectedDrugDevelopmentStages}
                      onChange={setSelectedDrugDevelopmentStages}
                      options={facetData.drugDevelopmentStages.options}
                      counts={facetData.drugDevelopmentStages.counts}
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="Molecule Type"
                      values={selectedMoleculeTypes}
                      onChange={setSelectedMoleculeTypes}
                      options={facetData.moleculeTypes.options}
                      counts={facetData.moleculeTypes.counts}
                      showCheckboxes
                    />
                    <SingleSelectionAutocomplete
                      label="Molecule Match"
                      value={moleculeMatch}
                      onChange={(value) => setMoleculeMatch(normalizeGlobaldataMoleculeMatch(value))}
                      options={GLOBALDATA_MOLECULE_MATCH_OPTIONS}
                    />
                    <MultiSelectionAutocomplete
                      label="Controlled Substance"
                      values={selectedControlledSubstances}
                      onChange={setSelectedControlledSubstances}
                      options={facetData.controlledSubstances.options}
                      counts={facetData.controlledSubstances.counts}
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="Drug Type"
                      values={selectedDrugTypes}
                      onChange={setSelectedDrugTypes}
                      options={facetData.drugTypes.options}
                      counts={facetData.drugTypes.counts}
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="Drug Geography"
                      values={selectedDrugGeographies}
                      onChange={setSelectedDrugGeographies}
                      options={facetData.drugGeographies.options}
                      counts={facetData.drugGeographies.counts}
                      showCheckboxes
                    />
                    <MultiSelectionAutocomplete
                      label="Route of Administration"
                      values={selectedRouteOfAdministrations}
                      onChange={setSelectedRouteOfAdministrations}
                      options={facetData.routeOfAdministrations.options}
                      counts={facetData.routeOfAdministrations.counts}
                      showCheckboxes
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Paper>

            <Paper sx={SIDEBAR_CARD_SX}>
              <Box
                sx={{
                  width: "100%",
                  display: "grid",
                  gap: 1,
                }}
              >
                <Accordion disableGutters elevation={0} sx={SIDEBAR_ACCORDION_SX}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreRoundedIcon />}
                    sx={SIDEBAR_ACCORDION_SUMMARY_SX}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#1f2937" }}>
                        Sorting
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                        Ordering and display direction
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ ...SIDEBAR_ACCORDION_DETAILS_SX, gap: 1.25 }}
                  >
                    <SingleSelectionAutocomplete
                      label="Sort By"
                      value={sortBy}
                      onChange={setSortBy}
                      options={GLOBALDATA_SORT_OPTIONS}
                    />
                    <SingleSelectionAutocomplete
                      label="Direction"
                      value={sortDirection}
                      onChange={setSortDirection}
                      options={GLOBALDATA_DIRECTION_OPTIONS}
                    />
                  </AccordionDetails>
                </Accordion>

              </Box>
            </Paper>

            <Paper
              sx={SIDEBAR_CARD_SX}
            >
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RestartAltRoundedIcon />}
                onClick={resetAll}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 999,
                }}
              >
                Reset Filters
              </Button>
            </Paper>
          </Box>
        </Box>

        <Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading && !summary ? (
            <Paper
              sx={{
                p: 4,
                borderRadius: 3,
                border: "1px solid rgba(15, 23, 42, 0.06)",
                textAlign: "center",
              }}
            >
              <CircularProgress size={30} />
              <Typography sx={{ mt: 1.25, color: "#64748b" }}>
                Loading GlobalData workspace...
              </Typography>
            </Paper>
          ) : rightPanelView === "visualisations" ? (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", xl: "repeat(4, minmax(0, 1fr))" },
                  gap: 1.25,
                }}
              >
                <SummaryCard label="Companies" value={summary?.totalCompanies || 0} />
                <SummaryCard
                  label={matchedSummaryLabel}
                  value={matchedSummaryCount}
                  tone="matched"
                  onClick={() => {
                    setMatchedState(
                      activeMatchTarget === "companyhouse"
                        ? "companyhouse_matched"
                        : "crunchbase_matched"
                    );
                  }}
                />
                <SummaryCard
                  label={unmatchedSummaryLabel}
                  value={unmatchedSummaryCount}
                  tone="unmatched"
                  onClick={() => {
                    setMatchedState(
                      activeMatchTarget === "companyhouse"
                        ? "companyhouse_unmatched"
                        : "crunchbase_unmatched"
                    );
                  }}
                />
                <SummaryCard
                  label="Reachable Drugs"
                  value={summary?.totalDrugs || 0}
                  tone="drugs"
                />
              </Box>

              <Accordion
                expanded={criteriaExtractionExpanded}
                onChange={(_, nextExpanded) => setCriteriaExtractionExpanded(nextExpanded)}
                disableGutters
                sx={{
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  borderRadius: "18px !important",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon />}
                  sx={{
                    px: 1.5,
                    minHeight: 56,
                    "& .MuiAccordionSummary-content": {
                      my: 1,
                    },
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                      GlobalData Criteria Extraction
                    </Typography>
                    <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>
                      Tabular market research summaries for the active GlobalData criteria.
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      alignItems: "center",
                      mb: 1.5,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={openCriteriaExtractionDownloadDialog}
                      disabled={criteriaExtractionExporting}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 999,
                      }}
                    >
                      {criteriaExtractionExporting
                        ? "Downloading workbook..."
                        : "Download extraction workbook"}
                    </Button>
                    {criteriaExtraction ? (
                      <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>
                        {`${formatCount(criteriaExtraction.totalAssets || 0)} assets across ${formatCount(
                          criteriaExtraction.totalCompanies || 0
                        )} companies`}
                      </Typography>
                    ) : null}
                  </Box>

                  {criteriaExtractionError ? (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      {criteriaExtractionError}
                    </Alert>
                  ) : null}

                  {!criteriaExtraction && criteriaExtractionLoading ? (
                    <Box
                      sx={{
                        minHeight: 160,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px dashed rgba(15, 23, 42, 0.16)",
                        borderRadius: 2,
                        color: "#64748b",
                        backgroundColor: "#f8fafc",
                        px: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: "0.92rem" }}>
                        Loading criteria extraction tables for the current GlobalData selection.
                      </Typography>
                    </Box>
                  ) : null}

                  {criteriaExtraction ? (
                    <Box sx={{ display: "grid", gap: 1.25, minWidth: 0, width: "100%", maxWidth: "100%" }}>
                      {criteriaExtractionSections.map((section) => (
                        <CriteriaExtractionSection
                          key={section.id}
                          title={section.title}
                          subtitle={section.subtitle}
                          columns={section.columns}
                          rows={section.rows}
                          expanded={Boolean(expandedCriteriaSections[section.id])}
                          onChange={(nextExpanded) =>
                            setExpandedCriteriaSections((current) => ({
                              ...current,
                              [section.id]: nextExpanded,
                            }))
                          }
                          emptyLabel={section.emptyLabel}
                          minHeight={section.minHeight}
                          maxHeight={section.maxHeight}
                        />
                      ))}
                    </Box>
                  ) : null}
                </AccordionDetails>
              </Accordion>

              <Dialog
                open={criteriaExtractionDownloadDialogOpen}
                onClose={closeCriteriaExtractionDownloadDialog}
                fullWidth
                maxWidth="xs"
              >
                <DialogTitle>Download Extraction Workbook</DialogTitle>
                <DialogContent>
                  <Box sx={{ pt: 1 }}>
                    <TextField
                      autoFocus
                      fullWidth
                      size="small"
                      label="Workbook Name"
                      value={criteriaExtractionFileName}
                      onChange={(event) => setCriteriaExtractionFileName(event.target.value)}
                      helperText="Downloads as an .xlsx workbook."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FileDownloadOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button onClick={closeCriteriaExtractionDownloadDialog} disabled={criteriaExtractionExporting}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={submitCriteriaExtractionDownload}
                    disabled={criteriaExtractionExporting}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    {criteriaExtractionExporting ? "Downloading..." : "Download"}
                  </Button>
                </DialogActions>
              </Dialog>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.5,
                }}
              >
                <BucketChart
                  title="CRO Tier Distribution"
                  buckets={summary?.croTierBuckets}
                  emptyLabel="No CRO tier distribution available."
                  onSelect={(value) => handleBucketSelection("croTiers", value)}
                  color="#4f46e5"
                  height={320}
                  yAxisLabel="Company Count"
                />
                <BucketChart
                  title="CDMO Tier Distribution"
                  buckets={summary?.cdmoTierBuckets}
                  emptyLabel="No CDMO tier distribution available."
                  onSelect={(value) => handleBucketSelection("cdmoTiers", value)}
                  color="#0f766e"
                  height={320}
                  yAxisLabel="Company Count"
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 1.5,
                }}
              >
                <BucketChart
                  title="Geographic Distribution"
                  buckets={summary?.headquartersBuckets}
                  emptyLabel="No headquarters distribution available."
                  onSelect={(value) => handleBucketSelection("headquarters", value)}
                  color="#4caf50"
                  height={340}
                  forceAxisAngle={-45}
                  yAxisLabel="Company Count"
                />
                <BucketChart
                  title="Entity Type"
                  buckets={summary?.entityTypeBuckets}
                  emptyLabel="No entity type distribution available."
                  onSelect={(value) => handleBucketSelection("entityTypes", value)}
                  color="#1976d2"
                  yAxisLabel="Company Count"
                />
                <BucketChart
                  title="Highest Development Stage"
                  buckets={summary?.companyStageBuckets}
                  emptyLabel="No stage distribution available."
                  onSelect={(value) => handleBucketSelection("companyStages", value)}
                  color="#9c27b0"
                  forceAxisAngle={-45}
                  yAxisLabel="Company Count"
                />
                <BucketChart
                  title="Therapeutic Areas"
                  buckets={summary?.therapyAreaBuckets}
                  emptyLabel="No therapy area distribution available."
                  onSelect={(value) => handleBucketSelection("therapyAreas", value)}
                  color="#14b8a6"
                  forceAxisAngle={-45}
                  yAxisLabel="Drug Count"
                />
                <BucketChart
                  title="Drug Development Stage"
                  buckets={summary?.drugDevelopmentStageBuckets}
                  emptyLabel="No drug stage distribution available."
                  onSelect={(value) => handleBucketSelection("drugDevelopmentStages", value)}
                  color="#0f766e"
                  yAxisLabel="Drug Count"
                  yAxisWidth={88}
                  yAxisLabelOffset={16}
                />
                <BucketChart
                  title="Molecule Type"
                  buckets={summary?.moleculeTypeBuckets}
                  emptyLabel="No molecule type distribution available."
                  onSelect={(value) => handleBucketSelection("moleculeTypes", value)}
                  color="#3f51b5"
                  yAxisLabel="Drug Count"
                  yAxisWidth={88}
                  yAxisLabelOffset={16}
                />
                <BucketChart
                  title="Controlled Substance"
                  buckets={summary?.controlledSubstanceBuckets}
                  emptyLabel="No controlled substance distribution available."
                  onSelect={(value) => handleBucketSelection("controlledSubstances", value)}
                  color="#6d28d9"
                  yAxisLabel="Drug Count"
                />
              </Box>

              <Box sx={{ mt: 0.5 }}>
                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                  Active Filter Snapshot
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {matchedState !== "all" && (
                    <Chip
                      label={
                        matchedState === "crunchbase_matched"
                          ? "Matched to Crunchbase only"
                          : matchedState === "crunchbase_unmatched"
                          ? "Unmatched to Crunchbase only"
                          : matchedState === "companyhouse_matched"
                          ? "Matched to Company House only"
                          : matchedState === "companyhouse_unmatched"
                          ? "Unmatched to Company House only"
                          : "Unmatched across all datasets"
                      }
                      onDelete={() => setMatchedState("all")}
                    />
                  )}
                  {clientState !== "all" && (
                    <Chip
                      label={getClientStateFilterLabel(clientState)}
                      onDelete={() => setClientState("all")}
                    />
                  )}
                  {companyListView === COMPANY_LIST_VIEW_ASSIGNMENTS && (
                    <Chip
                      label="Assignments"
                      onDelete={() => setCompanyListView(COMPANY_LIST_VIEW_OVERVIEW)}
                    />
                  )}
                  {selectedCroTiers.map((value) => (
                    <Chip
                      key={`cro-tier-${value}`}
                      label={`CRO: ${value}`}
                      onDelete={() =>
                        setSelectedCroTiers((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedCdmoTiers.map((value) => (
                    <Chip
                      key={`cdmo-tier-${value}`}
                      label={`CDMO: ${value}`}
                      onDelete={() =>
                        setSelectedCdmoTiers((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {debouncedSearchTerm && (
                    <Chip label={`Search: ${debouncedSearchTerm}`} onDelete={() => setSearchTerm("")} />
                  )}
                  {selectedHeadquarters.map((value) => (
                    <Chip
                      key={`hq-${value}`}
                      label={`HQ: ${value}`}
                      onDelete={() =>
                        setSelectedHeadquarters((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedEntityTypes.map((value) => (
                    <Chip
                      key={`entity-${value}`}
                      label={`Entity: ${value}`}
                      onDelete={() =>
                        setSelectedEntityTypes((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedRelationshipTypes.map((value) => (
                    <Chip
                      key={`relationship-${value}`}
                      label={`Relationship: ${value}`}
                      onDelete={() =>
                        setSelectedRelationshipTypes((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedCompanyStages.map((value) => (
                    <Chip
                      key={`stage-${value}`}
                      label={`Stage: ${value}`}
                      onDelete={() =>
                        setSelectedCompanyStages((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedTherapyAreas.map((value) => (
                    <Chip
                      key={`therapy-${value}`}
                      label={`Therapy: ${value}`}
                      onDelete={() =>
                        setSelectedTherapyAreas((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedDrugDevelopmentStages.map((value) => (
                    <Chip
                      key={`drug-stage-${value}`}
                      label={`Drug Stage: ${value}`}
                      onDelete={() =>
                        setSelectedDrugDevelopmentStages((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedMoleculeTypes.map((value) => (
                    <Chip
                      key={`molecule-${value}`}
                      label={`Molecule: ${value}`}
                      onDelete={() =>
                        setSelectedMoleculeTypes((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedControlledSubstances.map((value) => (
                    <Chip
                      key={`controlled-${value}`}
                      label={`Controlled: ${value}`}
                      onDelete={() =>
                        setSelectedControlledSubstances((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedDrugTypes.map((value) => (
                    <Chip
                      key={`drug-type-${value}`}
                      label={`Drug Type: ${value}`}
                      onDelete={() =>
                        setSelectedDrugTypes((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedDrugGeographies.map((value) => (
                    <Chip
                      key={`drug-geography-${value}`}
                      label={`Geography: ${value}`}
                      onDelete={() =>
                        setSelectedDrugGeographies((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {selectedRouteOfAdministrations.map((value) => (
                    <Chip
                      key={`route-${value}`}
                      label={`Route: ${value}`}
                      onDelete={() =>
                        setSelectedRouteOfAdministrations((current) =>
                          current.filter((entry) => entry !== value)
                        )
                      }
                    />
                  ))}
                  {matchedState === "all" &&
                    clientState === "all" &&
                    companyListView === COMPANY_LIST_VIEW_OVERVIEW &&
                    !debouncedSearchTerm &&
                    selectedHeadquarters.length === 0 &&
                    selectedEntityTypes.length === 0 &&
                    selectedRelationshipTypes.length === 0 &&
                    selectedCompanyStages.length === 0 &&
                    selectedCroTiers.length === 0 &&
                    selectedCdmoTiers.length === 0 &&
                    selectedTherapyAreas.length === 0 &&
                    selectedDrugDevelopmentStages.length === 0 &&
                    selectedMoleculeTypes.length === 0 &&
                    selectedControlledSubstances.length === 0 &&
                    selectedDrugTypes.length === 0 &&
                    selectedDrugGeographies.length === 0 &&
                    selectedRouteOfAdministrations.length === 0 && (
                      <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
                        No filters applied. Charts and cards reflect the current GlobalData
                        search universe.
                      </Typography>
                    )}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                Total GlobalData companies: {formatCount(total)}
              </Typography>

              {items.length === 0 && !loading ? (
                <Typography sx={{ mt: 3, fontWeight: 700, color: "#1f2937" }}>
                  {companyListView === COMPANY_LIST_VIEW_ASSIGNMENTS
                    ? "No assigned GlobalData companies match the current filters."
                    : "No GlobalData companies match the current filters."}
                </Typography>
              ) : (
                items.map((company) => (
                  <GlobaldataCompanyCard
                    key={company.companyName}
                    company={company}
                    postJson={postJson}
                    deleteJson={deleteJson}
                    getApiJson={getApiJson}
                    onOpenCrunchbaseMatch={onOpenCrunchbaseMatch}
                    onOpenCompanyHouseMatch={onOpenCompanyHouseMatch}
                    assignment={getAssignmentForCompanyName?.(company.companyName) || null}
                    canManageAssignments={canManageAssignments}
                    assignableUsers={assignableUsers}
                    assignableUsersLoading={assignableUsersLoading}
                    onAssignmentsChanged={onAssignmentsChanged}
                    onContactedChanged={handleContactedChanged}
                  />
                ))
              )}

              {hasMore && (
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => loadSearchPage(page + 1, true)}
                    disabled={loadingMore}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
